import { Button, DialogContent, DialogActions, TextField, CircularProgress, MenuItem, FormControl, Select, InputLabel } from "@mui/material";
import { useStore } from "../../store/store";
import axios from "axios";
import { SelectTemplate } from "../selectTemplate/SelectTemplate";
import { useForm, Controller } from "react-hook-form";
import { useState, useEffect } from "react";
import { useUser } from "../../api/useAuth";
import { useRelatedRecords } from "../../api/useRelatedRecords";

type SMSDialogProps = {
    context: any;
    onClose: () => void;
}

export const SMSDialog = ({ context, onClose }: SMSDialogProps) => {
    const { data: user } = useUser();
    const { notifications } = useStore();
    const [isLoading, setIsLoading] = useState(false);
    
    // Determine entity type and get relatives if needed
    const entityType = context.entity || "leads";
    const isFromTreatment = entityType === "treatments";
    const { data: relatives } = useRelatedRecords(
        "relatives", 
        "*.*", 
        "client_id", 
        isFromTreatment ? context?.item?.client?.id : null
    );

    const phones = possiblePhones(context?.item, isFromTreatment, relatives?.items);
    
    const entityForm = useForm({
        defaultValues: {
            sms: "",
            phone_to_send: "",
        },
    });

    useEffect(() => {
        if (phones?.[0]?.phone) {
            entityForm.setValue("phone_to_send", phones[0].phone);
        }
    }, [phones]);

    const handleSend = async (formData: { sms: string; phone_to_send: string }) => {
        notifications.setNotification({
            isOpen: false,
        });
        setIsLoading(true);

        // For leads, use the original phone logic, for treatments use selected phone
        const phoneToUse = isFromTreatment ? formData.phone_to_send : context.item.phone;
        
        // Strip Portuguese special characters to avoid extra SMS costs
        const normalizedSmsContent = stripPortugueseCharacters(formData.sms);

        try {
            await axios({
                method: "POST",
                url: "https://hook.eu1.make.com/6yi4p9jfxo7tveao1t650gtm3ggsqpsx",
                data: {
                    type: "sms",
                    content: normalizedSmsContent,
                    id: context.item.id,
                    entity: entityType,
                    phoneNumber: phoneToUse,
                    user_id: user?.id,
                },
            });

            notifications.setNotification({
                isOpen: true,
                message: `SMS enviado com sucesso`,
                severity: "success",
            });

            entityForm.reset();
            onClose();
        } catch (error: any) {
            const errorMessage = error?.response?.data?.errorMessage || "Erro ao enviar SMS";
            notifications.setNotification({
                isOpen: true,
                message: errorMessage,
                severity: "error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={entityForm.handleSubmit(handleSend)}>
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <SelectTemplate field="sms" entity={entityType} entityForm={entityForm as any} />
                <Controller
                    name="sms"
                    control={entityForm.control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            autoFocus
                            margin="dense"
                            label="Message"
                            type="text"
                            fullWidth
                            multiline
                            rows={6}
                        />
                    )}
                />
                {isFromTreatment && phones && phones.length > 0 && (
                    <Controller
                        name="phone_to_send"
                        control={entityForm.control}
                        render={({ field: { onChange, value, ...field } }) => (
                            <FormControl fullWidth>
                                <InputLabel>Telefone para enviar SMS</InputLabel>
                                <Select
                                    {...field}
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                    label="Telefone para enviar SMS"
                                >
                                    {phones.map((phone: any) => (
                                        <MenuItem key={phone.phone} value={phone.phone}>
                                            {phone.name} | {phone.phone} ({phone.type === "relative" ? "Parente" : "Cliente"})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    Cancelar
                </Button>
                <Button type="submit">{isLoading ? <CircularProgress color="inherit" size={24} /> : "Enviar"}</Button>
            </DialogActions>
        </form>
    );
};

const stripPortugueseCharacters = (text: string) => {
    if (!text) return text;
    
    // Map of Portuguese special characters to their basic equivalents
    const charMap: { [key: string]: string } = {
        'á': 'a', 'à': 'a', 'ã': 'a', 'â': 'a', 'ä': 'a',
        'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e',
        'í': 'i', 'ì': 'i', 'î': 'i', 'ï': 'i',
        'ó': 'o', 'ò': 'o', 'õ': 'o', 'ô': 'o', 'ö': 'o',
        'ú': 'u', 'ù': 'u', 'û': 'u', 'ü': 'u',
        'ç': 'c',
        'ñ': 'n',
        // Uppercase versions
        'Á': 'A', 'À': 'A', 'Ã': 'A', 'Â': 'A', 'Ä': 'A',
        'É': 'E', 'È': 'E', 'Ê': 'E', 'Ë': 'E',
        'Í': 'I', 'Ì': 'I', 'Î': 'I', 'Ï': 'I',
        'Ó': 'O', 'Ò': 'O', 'Õ': 'O', 'Ô': 'O', 'Ö': 'O',
        'Ú': 'U', 'Ù': 'U', 'Û': 'U', 'Ü': 'U',
        'Ç': 'C',
        'Ñ': 'N'
    };
    
    return text.replace(/[áàãâäéèêëíìîïóòõôöúùûüçñÁÀÃÂÄÉÈÊËÍÌÎÏÓÒÕÔÖÚÙÛÜÇÑ]/g, (match) => charMap[match] || match);
};

const possiblePhones = (item: any, isFromTreatment: boolean, relatives?: { name: string; telemovel: string }[]) => {
    if (!item) return [];

    const possiblePhones = [];

    if (isFromTreatment) {
        // For treatments, get phone from client
        if (item.client?.user_id?.phone) {
            possiblePhones.push({
                type: "client",
                phone: item.client.user_id.phone,
                name: `${item.client.user_id.first_name} ${item.client.user_id.last_name}`,
            });
        }
        
        // Add relatives phones
        if (relatives && relatives.length > 0) {
            relatives.forEach((relative: any) => {
                if (relative.telemovel) {
                    possiblePhones.push({
                        type: "relative",
                        phone: relative.telemovel,
                        name: `${relative.Nome}`,
                    });
                }
            });
        }
    } else {
        // For leads, use the direct phone from item
        if (item.phone) {
            possiblePhones.push({
                type: "client",
                phone: item.phone,
                name: `${item.first_name || ''} ${item.last_name || ''}`.trim(),
            });
        }
    }

    return possiblePhones;
};
